import { useState, useEffect, forwardRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import {
  Button,
  Grid,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  Snackbar,
  Link,
  CircularProgress,
  Stack,
  Box,
  useTheme,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';

import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import authService from '@shared/services/authService';
import { onUserLogin } from '../../redux/actions';
import * as Yup from 'yup';
import { setUser } from '@shared/helpers/auth';
import StyledTextField from '@shared/components/StyledTextField';

import {
  ASSIST_SEARCH,
  FLIGHTS_SEARCH,
  HOTELS_SEARCH,
  HOTELS,
  ASSIST,
  FLIGHTS,
  REGISTER,
} from '@shared/helpers/routes';

const ROUTE_REDIRECTS = [
  { route: HOTELS_SEARCH, redirect: backPage => backPage },
  { route: FLIGHTS_SEARCH, redirect: backPage => backPage },
  { route: ASSIST_SEARCH, redirect: backPage => backPage },

  { route: HOTELS, redirect: () => HOTELS },
  { route: FLIGHTS, redirect: () => FLIGHTS },
  { route: ASSIST, redirect: () => ASSIST },
];

const getRedirectPath = backPage => {
  if (!backPage) {
    return HOTELS;
  }

  const match = ROUTE_REDIRECTS.find(({ route }) => backPage.startsWith(route));

  if (!match) {
    return HOTELS;
  }

  return match.redirect(backPage);
};

const validationSchema = Yup.object().shape({
  user: Yup.string().required('El campo de email es obligatorio'),
  password: Yup.string().required('El campo de contraseña es obligatorio'),
});

// Componente Alert seguro
const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

export default function Login() {
  const dispatch = useDispatch();
  const { site_logos, site_functions } = useSelector(
    store => store.pageConfiguration
  );
  const history = useHistory();
  const backPage = history.location?.state?.from?.pathname;

  const theme = useTheme();

  const [showPassword, setShowPassword] = useState(false);
  const [openAlert, setOpenAlert] = useState({
    message: '',
    isVisible: false,
  });
  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik({
    initialValues: { user: '', password: '' },
    validationSchema: validationSchema,
    onSubmit: async values => {
      if (formik.errors.user || formik.errors.password) return;

      if (values.user && values.password) {
        setIsLoading(true);
        authService
          .login(values)
          .then(response => {
            setUser(response);
            dispatch(onUserLogin(response));
            history.push(getRedirectPath(backPage));
          })
          .catch(({ response }) => {
            setIsLoading(false);
            handleOpenAlert(response?.data?.message || 'Error desconocido');
          });
      }
    },
  });

  useEffect(() => {
    const validateLogin = async () => {
      const token = await setUser();
      if (token) history.push(HOTELS);
    };
    validateLogin();
  }, []);

  const handleOpenAlert = message => {
    setOpenAlert({
      message: message || 'Error desconocido',
      isVisible: true,
    });
  };

  const handleCloseAlert = () => {
    setOpenAlert(prev => ({ ...prev, isVisible: false }));
  };

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      sx={{ height: '111vh', backgroundColor: 'rgba(0, 0, 0, 0.1)' }}
    >
      <Paper
        elevation={1}
        sx={{
          width: { xs: '100%', sm: '50%' },
          height: { xs: 'auto', sm: 'initial' },
        }}
      >
        {/* Header visible solo en mobile */}
        <Grid
          item
          container
          alignItems="center"
          justifyContent="center"
          sx={{
            display: { xs: 'flex', sm: 'none' },
            p: 2,
            backgroundColor: 'primary.main',
          }}
        >
          <Grid item sx={{ width: '70%' }}>
            <Link href="/">
              <img src={site_logos.login} width="100%" />
            </Link>
          </Grid>
        </Grid>

        {/* Contenido */}
        <Grid container justifyContent="space-between" alignItems="center">
          {/* Formulario */}
          <Grid item xs={12} md={6} sx={{ p: 2 }}>
            <Typography
              variant="h6"
              align="center"
              gutterBottom
              sx={{
                fontSize: 'clamp(1.2rem, 4vw, 1.8rem)',
                fontWeight: 600,
                color: theme.primary,
                mb: { xs: 2, sm: 3 },
              }}
            >
              Iniciar sesión
            </Typography>

            <form onSubmit={formik.handleSubmit}>
              <Stack spacing={2}>
                <StyledTextField
                  label="Email"
                  name="user"
                  type="email"
                  formik={formik}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <PersonOutlineIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                <StyledTextField
                  label="Contraseña"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  formik={formik}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityIcon />
                          ) : (
                            <VisibilityOffIcon sx={{ marginLeft: '-4px' }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  type="submit"
                  sx={{
                    fontSize: 'clamp(1rem, 2.5vw, 18px)',
                    background: theme.primary,
                    color: theme.text1,
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 2,
                    '&:hover': {
                      background: theme.primary,
                      color: theme.text1,
                    },
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={25} sx={{ color: theme.text1 }} />
                  ) : (
                    'Iniciar Sesión'
                  )}
                </Button>
              </Stack>
            </form>

            <Box
              component={Link}
              href="/recover"
              id="forgotPassword"
              sx={{
                display: 'block',
                fontFamily: 'Open Sans, sans-serif',
                width: '50%',
                mx: 'auto',
                mt: 3,
                px: 3,
                py: 1,
                fontSize: { xs: '14px', sm: '16px' },
                color: '#000',
                backgroundColor: '#fff',
                border: `1px solid ${theme.tertiary}`,
                borderRadius: '1rem',
                textDecoration: 'none',
                transition: 'all 0.2s ease-in-out',
                textAlign: 'center',
                minWidth: 'max-content',
                '&:hover': { opacity: 0.8 },
              }}
            >
              Olvidé mi Contraseña
            </Box>

            {site_functions?.userRegister && (
              <Stack spacing={2} sx={{ mt: 4 }}>
                <Typography variant="subtitle2" align="center">
                  ¿No estás Registrado?
                </Typography>
                <Button
                  sx={{ background: theme.primary }}
                  variant="contained"
                  id="register"
                  size="large"
                  fullWidth
                  onClick={() => history.push(REGISTER)}
                >
                  Registrate!
                </Button>
              </Stack>
            )}
          </Grid>

          {/* Imagen lateral (desktop) */}
          <Grid
            item
            xs={6}
            container
            alignItems="center"
            justifyContent="center"
            sx={{
              background: theme.primary,
              height: '80vh',
              display: { xs: 'none', sm: 'flex' },
            }}
          >
            <Link href="/">
              <img src={site_logos.login} style={{ width: '200px' }} />
            </Link>
          </Grid>
        </Grid>
      </Paper>

      {openAlert.isVisible && openAlert.message && (
        <Snackbar
          open={openAlert.isVisible}
          autoHideDuration={3000}
          onClose={handleCloseAlert}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseAlert} severity="error">
            {openAlert.message}
          </Alert>
        </Snackbar>
      )}
    </Grid>
  );
}
